import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Avatar,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Select,
  MenuItem,
} from "@mui/material";
import { Formik, Form } from "formik";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import DownloadIcon from "@mui/icons-material/Download";
import { useLazyGetImageQuery } from "@/api/services/profile/profile_/FetchImageApi";

import { StyledButton } from "@/components";
import { CustomAlert } from "@/components";
import FormikSelectInput from "@/components/forms/FormikSelectInput";
import FormikMonthYearPicker from "@/components/forms/FormikMonthYearPicker";
import CommonDataTable1 from "@/components/dataTableComponents/NewDatatablePagination";
import profileimage from "@/assets/images/boypic.jpg";
import {
  useGetDivisionHeadQuery,
  useGetHrResourceListQuery,
  useLazyGetReviewerByDHQuery,
  useLazyGetRmByReviewerQuery,
  useLazyGetEmployeesByRmQuery,
  useUpdateRmStatusMutation,
  useLazyRAExportListQuery,
} from "@/api/services/resourceAllocation/resourceAllocation";
import { toast } from "@/components/toastify";

const STATUS_CONFIG = {
  Pending: {
    label: "Pending",
    icon: <AccessTimeOutlinedIcon sx={{ fontSize: 14 }} />,
    chipSx: { backgroundColor: "#FFF7ED", color: "#C2410C" },
    avatarRing: "#CBD5E1",
  },
  Saved: {
    label: "Saved",
    icon: <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />,
    chipSx: { backgroundColor: "#E5E7EB", color: "#374151" },
    avatarRing: "#9CA3AF",
  },
  Submitted: {
    label: "Submitted",
    icon: <DoneAllIcon sx={{ fontSize: 14 }} />,
    chipSx: { backgroundColor: "#D1FAE5", color: "#065F46" },
    avatarRing: "#10B981",
  },
  Completed: {
    label: "Completed",
    icon: <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />,
    chipSx: { backgroundColor: "#D1FAE5", color: "#065F46" },
    avatarRing: "#10B981",
  },
};

const now = new Date();
const currentMonthYear = `${String(now.getMonth() + 1).padStart(2, "0")}-${now.getFullYear()}`;

const INITIAL_FILTERS = {
  monthYear: currentMonthYear,
  divisionHeadId: "",
  reviewerId: "",
  reportingManagerId: "",
  employeeId: "",
  rmStatus: "",
  leaveStatus: "",
  search: "",
};

const STATUS_OPTIONS = [
  { value: "Pending", label: "Pending" },
  // { value: "Saved", label: "Saved" },
  { value: "Completed", label: "Submitted" },
];

const LEAVE_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];

const HRResourceAllocation = () => {
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [currentPage, setCurrentPage] = useState(0);
  const [per_page, setPerPage] = useState(10);
  const [confirmModal, setConfirmModal] = useState(false);
  const [confirmRow, setConfirmRow] = useState(null);
  const [searchInput, setSearchInput] = useState("");

  // API call
  const {
    data: listData,
    isLoading,
    isFetching,
  } = useGetHrResourceListQuery({
    page: currentPage,
    size: per_page,
    employeeId: filters.employeeId || undefined,
    monthYear: filters.monthYear || undefined,
    divisionHeadId: filters.divisionHeadId || undefined,
    reviewerId: filters.reviewerId || undefined,
    reportingManagerId: filters.reportingManagerId || undefined,
    rmStatus: filters.rmStatus || undefined,
    leaveStatus: filters.leaveStatus || undefined,
    search: filters.search || undefined,
  });

  const { data: divisionHeads } = useGetDivisionHeadQuery();
  const [fetchReviewers] = useLazyGetReviewerByDHQuery();
  const [fetchRmByReviewer] = useLazyGetRmByReviewerQuery();
  const [fetchEmployeesByRm] = useLazyGetEmployeesByRmQuery();
  const [updateRmStatus, { isLoading: updateLoading }] =
    useUpdateRmStatusMutation();
  const [exportList, { isLoading: exportLoading }] = useLazyRAExportListQuery();

  const [reviewerOptions, setReviewerOptions] = useState([]);
  const [rmOptions, setRmOptions] = useState([]);
  const [employeeOptions, setEmployeeOptions] = useState([]);

  const divisionHeadOptions = (divisionHeads?.data || divisionHeads || []).map(
    (item) => ({
      value: item.managerId || item.employeeId || item.id,
      label: `${item.empName || item.name} - ${item.managerId || item.employeeId || item.id}`,
    }),
  );

  // Profile images
  const [fetchImage] = useLazyGetImageQuery();
  const [imageMap, setImageMap] = useState({});
  const cachedImageMap = useRef({});
  const rows = listData?.content || listData?.data?.content || [];

  useEffect(() => {
    const fetchImages = async () => {
      if (!rows || rows.length === 0) return;
      const updatedMap = { ...cachedImageMap.current };
      for (const emp of rows) {
        const empId = emp.empId || emp.employeeId;
        if (!empId || updatedMap[empId]) continue;
        const result = { profile: null };
        try {
          if (emp.profile?.fileName && emp.profile?.folderName) {
            result.profile = await fetchImage({
              fileName: emp.profile.fileName,
              folderName: emp.profile.folderName,
            }).unwrap();
          }
        } catch (e) {
          // silent
        }
        updatedMap[empId] = result;
      }
      cachedImageMap.current = updatedMap;
      setImageMap(updatedMap);
    };
    if (rows.length > 0) fetchImages();
  }, [rows, fetchImage]);

  const totalPages = listData?.totalPages || listData?.data?.totalPages || 0;
  const totalRecords =
    listData?.totalElements || listData?.data?.totalElements || 0;

  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
  }, []);

  const handleSearchChange = useCallback((value) => {
    console.log("log", value);
    setSearchInput(value);

    if (value.length >= 2 || value === "") {
      setCurrentPage(0);
      setFilters((prev) => ({
        ...prev,
        search: value,
        page: 0, // reset both UI & API to 0
      }));
    }
  }, []);

  const handlePageSizeChange = useCallback((newPageSize) => {
    setPerPage(newPageSize);
    setCurrentPage(0);
  }, []);

  const handleExport = async () => {
    try {
      const blob = await exportList({
        //  employeeId: filters.employeeId || undefined,
        monthYear: filters.monthYear || undefined,
        divisionHeadId: filters.divisionHeadId || undefined,
        reviewerId: filters.reviewerId || undefined,
        reportingManagerId: filters.reportingManagerId || undefined,
        rmStatus: filters.rmStatus || undefined,
        leaveStatus: filters.leaveStatus || undefined,
      }).unwrap();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const now = new Date();
      const timestamp = now
        .toISOString()
        .replace(/[-:]/g, "")
        .replace("T", "_")
        .split(".")[0];
      a.download = `HR_Resource_Allocation_${timestamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast("success", " File Download successfully!");
    } catch (error) {
      console.error("Export failed", error);
    }
  };

  const columns = [
    {
      field: "employeeName",
      pinned: "left",
      headerName: "Employee",
      flex: 1.2,
      renderCell: (params) => {
        const emp = params.row;
        const empId = emp.empId || emp.employeeId;
        const status = emp.rmStatus || emp.status || "Pending";
        const statusCfg = STATUS_CONFIG[status] || STATUS_CONFIG.Pending;
        const profileUrl = imageMap[empId]?.profile || null;
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                p: "2px",
                borderRadius: "50%",
                background: `linear-gradient(135deg, ${statusCfg.avatarRing}, ${statusCfg.avatarRing}66)`,
                flexShrink: 0,
              }}
            >
              {/* <Avatar src={profileUrl || profileimage} alt={emp.employeeName || emp.name} sx={{ width: 36, height: 36, border: "2px solid #fff" }} /> */}
              <Avatar
                src={profileUrl || undefined}
                alt={emp?.employeeName || emp?.name}
                sx={{
                  width: 40,
                  height: 40,
                  border: "2px solid #fff",
                  bgcolor: profileUrl ? "transparent" : "#bdbdbd;",
                  fontSize: "16px",
                  fontWeight: 700,
                }}
              >
                {!profileUrl &&
                  (emp?.employeeName || emp?.name || "E")
                    .charAt(0)
                    .toUpperCase()}
              </Avatar>
            </Box>
            <Box>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "13px",
                  color: "#1E293B",
                  lineHeight: 1.3,
                }}
              >
                {emp.employeeName || emp.name || "Employee"}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "#64748B", fontWeight: 500 }}
              >
                {empId || "N/A"}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: "department",
      headerName: "Department",
      flex: 1,
      renderCell: (params) =>
        params.row.department || params.row.departmentName || "N/A",
    },
    {
      field: "designation",
      headerName: "Designation",
      flex: 1,
      renderCell: (params) => params.row.designation || "N/A",
    },
    {
      field: "divisionHeadId",
      headerName: "Division Head",
      flex: 1,
      renderCell: (params) =>
        params?.row.divisionName
          ? `${params.row.divisionName} - ${params.row.divisionHeadId}`
          : params.row.divisionHeadId || "-",
    },
    {
      field: "reviewerId",
      headerName: "Reviewer",
      flex: 1,
      renderCell: (params) =>
        params?.row.reviewerName
          ? `${params.row.reviewerName} - ${params.row.reviewerId}`
          : params.row.reviewerId || "-",
    },
    {
      field: "reportingManager",
      headerName: "Reporting Manager",
      flex: 1,
      renderCell: (params) =>
        params.row.reportingManagerName
          ? `${params.row.reportingManagerName} - ${params.row.reportingManagerId}`
          : params.row.rmName || "-",
    },
    {
      field: "lastWorkingDay",
      headerName: "Last Working Day",
      flex: 1,
      renderCell: (params) =>
        params.row.lastWorkingDay ? `${params.row.lastWorkingDay} ` : "-",
    },
    {
      field: "rmStatus",
      headerName: "Status",
      flex: 0.8,
      renderCell: (params) => {
        const status = params.row.rmStatus || params.row.status || "Pending";
        const statusCfg = STATUS_CONFIG[status] || STATUS_CONFIG.Pending;
        return (
          <Chip
            icon={statusCfg.icon}
            label={statusCfg.label}
            size="small"
            sx={{
              height: 26,
              fontSize: "12px",
              fontWeight: 600,
              ...statusCfg.chipSx,
            }}
          />
        );
      },
    },
    {
      field: "projects",
      headerName: "Projects",
      flex: 1.5,
      renderCell: (params) => {
        if (params.row.leaveStatus === "yes") {
          return (
            <Typography
              variant="caption"
              sx={{ color: "#64748B", fontWeight: 600 }}
            >
              {" "}
              On long leave
            </Typography>
          );
        }
        const allocations = params.row.projectDetails || [];
        if (allocations.length === 0) {
          return (
            <Typography
              variant="caption"
              sx={{ color: "#94A3B8", fontStyle: "italic" }}
            >
              No projects allocated
            </Typography>
          );
        }
        return (
          <Box
            sx={{
              display: "flex",
              flexWrap: "nowrap",
              gap: 0.75,
              alignItems: "center",
              overflowX: "auto",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            {allocations.map((a, i) => (
              <Chip
                key={i}
                label={`${a.project || a.projectName} (${a.projectPercentage || a.projectPercentage}%)`}
                size="small"
                sx={{
                  height: 24,
                  fontSize: "12px",
                  backgroundColor: "#F8FAFC",
                  color: "#334155",
                  fontWeight: 500,
                  border: "1px solid #E2E8F0",
                  flexShrink: 0,
                }}
              />
            ))}
          </Box>
        );
      },
    },
  ];

  const [visibleColumns, setVisibleColumns] = useState([]);

  useEffect(() => {
    setVisibleColumns(columns.map((col) => ({ ...col, visible: true })));
  }, []);

  const handleToggleColumn = (field) => {
    setVisibleColumns((prev) =>
      prev.map((col) =>
        col.field === field ? { ...col, visible: !col.visible } : col,
      ),
    );
  };

  const renderedColumns = visibleColumns.filter((col) => col.visible);

  const tableRows = rows.map((emp, index) => ({
    ...emp,
    id: emp.id || emp.empId || emp.employeeId || index,
  }));

  // Format monthYear for Formik picker (YYYY-MM) vs API (MM-YYYY)
  const formikMonthYear = filters.monthYear
    ? filters.monthYear.split("-").reverse().join("-")
    : "";

  return (
    <Box
      sx={{ mt: 2, p: 2.5, borderRadius: "16px", backgroundColor: "#ffffff" }}
    >
      <Typography
        variant="h6"
        sx={{ fontWeight: 700, color: "#1E293B", mb: 0.5 }}
      >
        HR Resource Allocation View
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        View all employee project allocations with status and details.
      </Typography>

      {/* Filters */}
      <Formik
        initialValues={{
          monthYear: formikMonthYear,
          divisionHeadId: "",
          reviewerId: "",
          reportingManagerId: "",
          employeeId: "",
          rmStatus: "",
          leaveStatus: "",
        }}
        enableReinitialize
        onSubmit={() => {}}
      >
        {({ setFieldValue, resetForm }) => (
          <Form>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                alignItems: "flex-end",
                mb: 2,
              }}
            >
              <Box sx={{ minWidth: 180 }}>
                <FormikMonthYearPicker
                  name="monthYear"
                  label="Month & Year"
                  pickerType="month"
                  placeholder="Select month"
                  onChange={(val) => {
                    setFieldValue("monthYear", val);
                    // Convert YYYY-MM to MM-YYYY for API
                    const parts = val ? val.split("-") : [];
                    const apiFormat =
                      parts.length === 2 ? `${parts[1]}-${parts[0]}` : "";
                    setFilters((prev) => ({ ...prev, monthYear: apiFormat }));
                    setCurrentPage(0);
                  }}
                />
              </Box>

              <Box sx={{ minWidth: 180 }}>
                <FormikSelectInput
                  name="divisionHeadId"
                  label="Division Head"
                  placeholder="All Division Heads"
                  options={divisionHeadOptions}
                  clear
                  search
                  onChange={async (e) => {
                    const val = e.target.value;
                    setFieldValue("divisionHeadId", val);
                    setFieldValue("reviewerId", "");
                    setFieldValue("reportingManagerId", "");
                    setFieldValue("employeeId", "");
                    setReviewerOptions([]);
                    setRmOptions([]);
                    setEmployeeOptions([]);
                    setFilters((prev) => ({
                      ...prev,
                      divisionHeadId: val,
                      reviewerId: "",
                      reportingManagerId: "",
                      employeeId: "",
                    }));
                    setCurrentPage(0);
                    if (val) {
                      try {
                        const res = await fetchReviewers(val).unwrap();
                        const list = res?.data || res || [];
                        setReviewerOptions(
                          list.map((item) => ({
                            value: item.managerId || item.employeeId || item.id,
                            label: item?.reviewerEmpIdName,
                          })),
                        );
                      } catch (e) {
                        /* silent */
                      }
                    }
                  }}
                />
              </Box>

              <Box sx={{ minWidth: 180 }}>
                <FormikSelectInput
                  name="reviewerId"
                  label="Reviewer"
                  placeholder="All Reviewers"
                  options={reviewerOptions}
                  clear
                  search
                  onChange={async (e) => {
                    const val = e.target.value;
                    setFieldValue("reviewerId", val);
                    setFieldValue("reportingManagerId", "");
                    setFieldValue("employeeId", "");
                    setRmOptions([]);
                    setEmployeeOptions([]);
                    setFilters((prev) => ({
                      ...prev,
                      reviewerId: val,
                      reportingManagerId: "",
                      employeeId: "",
                    }));
                    setCurrentPage(0);
                    if (val) {
                      try {
                        const res = await fetchRmByReviewer(val).unwrap();
                        const list = res?.data || res || [];
                        setRmOptions(
                          list.map((item) => ({
                            value: item.managerId || item.employeeId || item.id,
                            label: item?.reportingManagerEmpIdName,
                          })),
                        );
                      } catch (e) {
                        /* silent */
                      }
                    }
                  }}
                />
              </Box>

              <Box sx={{ minWidth: 180 }}>
                <FormikSelectInput
                  name="reportingManagerId"
                  label="Reporting Manager"
                  placeholder="All Managers"
                  options={rmOptions}
                  clear
                  search
                  onChange={async (e) => {
                    const val = e.target.value;
                    setFieldValue("reportingManagerId", val);
                    setFieldValue("employeeId", "");
                    setEmployeeOptions([]);
                    setFilters((prev) => ({
                      ...prev,
                      reportingManagerId: val,
                      employeeId: "",
                    }));
                    setCurrentPage(0);
                    if (val) {
                      try {
                        const res = await fetchEmployeesByRm(val).unwrap();
                        const list = res?.data || res || [];
                        setEmployeeOptions(
                          list.map((item) => ({
                            value: item.empId || item.employeeId || item.id,
                            label: `${item.employeeName || item.name} - ${item.empId || item.employeeId || item.id}`,
                          })),
                        );
                      } catch (e) {
                        /* silent */
                      }
                    }
                  }}
                />
              </Box>

              <Box sx={{ minWidth: 180 }}>
                <FormikSelectInput
                  name="employeeId"
                  label="Employee"
                  placeholder="All Employees"
                  options={employeeOptions}
                  clear
                  search
                  onChange={(e) => {
                    setFieldValue("employeeId", e.target.value);
                    setFilters((prev) => ({
                      ...prev,
                      employeeId: e.target.value,
                    }));
                    setCurrentPage(0);
                  }}
                />
              </Box>

              <Box sx={{ minWidth: 180 }}>
                <FormikSelectInput
                  name="rmStatus"
                  label="Status"
                  placeholder="All Status"
                  options={STATUS_OPTIONS}
                  clear
                  onChange={(e) => {
                    setFieldValue("rmStatus", e.target.value);
                    setFilters((prev) => ({
                      ...prev,
                      rmStatus: e.target.value,
                    }));
                    setCurrentPage(0);
                  }}
                />
              </Box>

              <Box sx={{ minWidth: 180 }}>
                <FormikSelectInput
                  name="leaveStatus"
                  label="Leave"
                  placeholder="All"
                  options={LEAVE_OPTIONS}
                  clear
                  onChange={(e) => {
                    setFieldValue("leaveStatus", e.target.value);
                    setFilters((prev) => ({
                      ...prev,
                      leaveStatus: e.target.value,
                    }));
                    setCurrentPage(0);
                  }}
                />
              </Box>

              <StyledButton
                label="Clear"
                variant="outlined"
                size="small"
                icon={<ClearAllIcon />}
                onClick={() => {
                  resetForm();
                  setFilters(INITIAL_FILTERS);
                  setReviewerOptions([]);
                  setRmOptions([]);
                  setEmployeeOptions([]);
                  setCurrentPage(0);
                }}
                sx={{
                  height: "36px",
                  borderRadius: "8px",
                  borderColor: "#CBD5E1",
                  color: "#64748B",
                  "&:hover": {
                    borderColor: "#94A3B8",
                    backgroundColor: "#F8FAFC",
                  },
                }}
              />

              <StyledButton
                label="Export"
                variant="contained"
                size="small"
                icon={<DownloadIcon />}
                onClick={handleExport}
                loading={exportLoading}
                sx={{
                  height: "36px",
                  borderRadius: "8px",
                  backgroundColor: "#305533",
                  "&:hover": { backgroundColor: "#1e3a21" },
                }}
              />
            </Box>
          </Form>
        )}
      </Formik>

      {/* Table */}
      <CommonDataTable1
        columns={renderedColumns.length > 0 ? renderedColumns : columns}
        rows={tableRows}
        search={true}
        checkbox={false}
        disableRowSelectionOnClick={true}
        handleToggleColumn={handleToggleColumn}
        visibleColumns={visibleColumns}
        colvisColumns={columns}
        pageCount={totalPages}
        totalRecords={totalRecords}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        currentPage={currentPage}
        currentPageSize={per_page}
        showEntriesClear={false}
        loading={isLoading || isFetching}
        searchValue={searchInput}
        onSearchChange={handleSearchChange}
      />

      {confirmModal && (
        <CustomAlert
          open={confirmModal}
          title="Confirm Status Change"
          message={`Are you sure you want to mark ${confirmRow?.name || "this employee"}'s status as Completed?`}
          cancelText="Cancel"
          submitText="Confirm"
          isSubmitting={updateLoading}
          onCancel={() => {
            setConfirmModal(false);
            setConfirmRow(null);
          }}
          onSubmit={async () => {
            try {
              await updateRmStatus({
                id: confirmRow.id,
                rmStatus: "Completed",
              }).unwrap();
              toast("success", "Status updated to Completed successfully!");
            } catch {
              toast("error", "Failed to update status.");
            }
            setConfirmModal(false);
            setConfirmRow(null);
          }}
        />
      )}
    </Box>
  );
};

export default HRResourceAllocation;
